<template>
  <main class="about-page">
    <div class="about-subnav-slot">
      <nav
        ref="subnav"
        class="about-subnav"
        :class="{ 'is-fixed': isSubnavFixed }"
        :aria-label="t('about.subnavAria')"
      >
        <div class="about-subnav__inner">
          <strong class="about-subnav__title">{{ t('about.subnavTitle') }}</strong>
          <div class="about-subnav__links">
            <a
              v-for="item in sectionNavigation"
              :key="item.id"
              :href="`#${item.id}`"
              :class="{ 'is-active': activeSection === item.id }"
              :aria-current="activeSection === item.id ? 'location' : undefined"
              @click.prevent="scrollToSection(item.id)"
            >
              {{ item.label }}
            </a>
          </div>
        </div>
      </nav>
    </div>

    <section id="company-profile" class="about-section company-profile">
      <div class="about-container company-profile__inner">
        <div class="company-profile__copy">
          <h1>{{ t('about.companyProfile') }}</h1>
          <div class="company-profile__body">
            <p v-for="paragraph in profileParagraphs" :key="paragraph">{{ paragraph }}</p>
          </div>
        </div>
        <img
          class="company-profile__image"
          src="/static/images/about-v2/company-profile.png"
          :alt="t('about.profileImageAlt')"
        />
      </div>
    </section>

    <section id="corporate-culture" class="about-section corporate-culture">
      <div class="about-container">
        <h2 class="section-title">{{ t('about.corporateCulture') }}</h2>
        <div class="culture-grid">
          <article v-for="item in cultureItems" :key="item.title" class="culture-card">
            <div class="culture-card__visual">
              <img :src="item.icon" :alt="`${item.title}${t('about.iconSuffix')}`" />
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section id="company-history" class="about-section company-history">
      <div class="about-container">
        <h2 class="section-title">{{ t('about.companyHistory') }}</h2>
        <ol class="history-timeline">
          <li
            v-for="item in historyItems"
            :key="item.year"
            class="history-item"
            :class="{ 'is-current': item.current }"
          >
            <span class="history-item__marker" aria-hidden="true"></span>
            <div class="history-item__content">
              <time :datetime="item.year">{{ item.year }}</time>
              <p v-for="event in item.events" :key="event">{{ event }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <section id="qualification" class="about-section qualification">
      <div class="about-container">
        <h2 class="section-title">{{ t('about.qualification') }}</h2>
        <ul class="qualification-grid">
          <li v-for="item in qualificationItems" :key="item.label" class="qualification-item">
            <div class="qualification-item__logo">
              <img :src="item.icon" :alt="item.label" />
            </div>
            <span>{{ item.label }}</span>
          </li>
        </ul>
      </div>
    </section>
  </main>
</template>

<script>
import {
  getAboutAnchorOffset,
  getActiveAboutSection,
  shouldFixAboutSubnav
} from '@/utils/about-navigation.cjs'
const { siteLanguageMixin } = require('@/utils/site-language.js')

export default {
  name: 'About',
  mixins: [siteLanguageMixin],
  data() {
    return {
      activeSection: 'company-profile',
      isSubnavFixed: false,
      scrollFrame: 0
    }
  },
  computed: {
    sectionNavigation() {
      return [
        { id: 'company-profile', label: this.t('about.companyProfile') },
        { id: 'corporate-culture', label: this.t('about.corporateCulture') },
        { id: 'company-history', label: this.t('about.navHistory') },
        { id: 'qualification', label: this.t('about.navQualification') }
      ]
    },
    profileParagraphs() {
      return [
        this.t('about.profileParagraph1'),
        this.t('about.profileParagraph2'),
        this.t('about.profileParagraph3')
      ]
    },
    cultureItems() {
      return [
        {
          title: this.t('about.mission'),
          description: this.t('about.missionDescription'),
          icon: '/static/images/about-v2/culture-mission.svg'
        },
        {
          title: this.t('about.vision'),
          description: this.t('about.visionDescription'),
          icon: '/static/images/about-v2/culture-vision.svg'
        },
        {
          title: this.t('about.values'),
          description: this.t('about.valuesDescription'),
          icon: '/static/images/about-v2/culture-values.svg'
        }
      ]
    },
    historyItems() {
      return [
        { year: '2013', events: [this.t('about.history2013Event1')] },
        {
          year: '2016',
          events: [
            this.t('about.history2016Event1'),
            this.t('about.history2016Event2'),
            this.t('about.history2016Event3')
          ]
        },
        {
          year: '2019',
          events: [
            this.t('about.history2019Event1'),
            this.t('about.history2019Event2')
          ]
        },
        {
          year: '2022',
          events: [
            this.t('about.history2022Event1'),
            this.t('about.history2022Event2'),
            this.t('about.history2022Event3')
          ]
        },
        {
          year: '2024',
          events: [
            this.t('about.history2024Event1'),
            this.t('about.history2024Event2'),
            this.t('about.history2024Event3')
          ]
        },
        {
          year: '2026',
          events: [this.t('about.history2026Event1')],
          current: true
        }
      ]
    },
    qualificationItems() {
      return [
        { label: this.t('about.qualificationFda'), icon: '/static/images/about-v2/qualification-fda.svg' },
        { label: this.t('about.qualificationIso9001'), icon: '/static/images/about-v2/qualification-iso9001.svg' },
        { label: this.t('about.qualificationRohs'), icon: '/static/images/about-v2/qualification-rohs.svg' },
        { label: this.t('about.qualificationSgs'), icon: '/static/images/about-v2/qualification-sgs.svg' },
        { label: this.t('about.qualificationCe'), icon: '/static/images/about-v2/qualification-ce.svg' },
        { label: this.t('about.qualificationClassII'), icon: '/static/images/about-v2/qualification-class-ii.svg' },
        { label: this.t('about.qualificationHighTech'), icon: '/static/images/about-v2/qualification-high-tech.svg' }
      ]
    }
  },
  watch: {
    currentLanguage() {
      this.updateDocumentTitle()
    }
  },
  mounted() {
    this.updateDocumentTitle()
    window.addEventListener('scroll', this.queueActiveSectionUpdate, { passive: true })
    window.addEventListener('resize', this.queueActiveSectionUpdate, { passive: true })
    this.$nextTick(this.updateActiveSection)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.queueActiveSectionUpdate)
    window.removeEventListener('resize', this.queueActiveSectionUpdate)
    window.cancelAnimationFrame(this.scrollFrame)
  },
  methods: {
    updateDocumentTitle() {
      document.title = this.t('about.documentTitle')
    },
    getNavigationOffset() {
      const subnavHeight = this.$refs.subnav ? this.$refs.subnav.offsetHeight : 44
      return getAboutAnchorOffset(this.getHeaderHeight(), subnavHeight)
    },
    getHeaderHeight() {
      const styles = window.getComputedStyle(document.documentElement)
      return Number.parseFloat(styles.getPropertyValue('--global-header-height')) || 44
    },
    scrollToSection(id) {
      const target = document.getElementById(id)
      if (!target) return

      const top = target.getBoundingClientRect().top + window.scrollY - this.getNavigationOffset()
      this.activeSection = id
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
    },
    queueActiveSectionUpdate() {
      if (this.scrollFrame) return
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0
        this.updateActiveSection()
      })
    },
    updateActiveSection() {
      this.isSubnavFixed = shouldFixAboutSubnav(window.scrollY, this.getHeaderHeight())
      const offsets = this.sectionNavigation
        .map(({ id }) => {
          const element = document.getElementById(id)
          return element ? { id, top: element.offsetTop } : null
        })
        .filter(Boolean)

      this.activeSection = getActiveAboutSection(
        offsets,
        window.scrollY + this.getNavigationOffset() + 1,
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2
      )
    }
  }
}
</script>

<style scoped>
.about-page {
  min-height: 100vh;
  min-width: 0;
  padding-top: var(--global-header-height, 44px);
  overflow-x: hidden;
  color: #000;
  background: #fff;
  font-family: MiSans, Arial, sans-serif;
}

.about-container,
.about-subnav__inner {
  width: min(calc(100% - 144px), 1296px);
  margin: 0 auto;
}

.about-subnav {
  position: relative;
  z-index: 900;
  height: 44px;
  color: #cfcfcf;
  background: #191b1d;
}

.about-subnav-slot {
  height: 44px;
}

.about-subnav.is-fixed {
  position: fixed;
  top: var(--global-header-offset, 44px);
  right: 0;
  left: 0;
}

.about-subnav__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.about-subnav__title {
  flex: 0 0 auto;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.about-subnav__links {
  display: flex;
  align-items: center;
  gap: 36px;
  height: 100%;
}

.about-subnav__links a {
  display: inline-flex;
  align-items: center;
  height: 100%;
  color: #cfcfcf;
  font-size: 13px;
  line-height: 1;
  text-decoration: none;
  transition: color 0.2s ease;
}

.about-subnav__links a:hover,
.about-subnav__links a:focus-visible,
.about-subnav__links a.is-active {
  color: #fff;
}

.about-subnav__links a:focus-visible {
  outline: 1px solid #fff;
  outline-offset: -4px;
}

.about-section {
  scroll-margin-top: calc(var(--global-header-height, 44px) + 44px);
}

.section-title,
.company-profile h1 {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.company-profile {
  height: 516px;
}

.company-profile__inner {
  display: grid;
  grid-template-columns: minmax(0, 605px) 636px;
  justify-content: space-between;
  gap: 24px;
  height: 100%;
  padding: 80px 0;
  box-sizing: border-box;
}

.company-profile__body {
  margin-top: 70px;
  font-size: 14px;
  line-height: 1.43;
}

.company-profile__body p {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  color: #000;
}

.company-profile__body p + p {
  margin-top: 10px;
}

.company-profile__image {
  display: block;
  width: 636px;
  height: 356px;
  object-fit: cover;
  border-radius: 28px;
}

.corporate-culture {
  min-height: 613px;
  padding: 80px 0;
}

.corporate-culture .section-title,
.company-history .section-title,
.qualification .section-title {
  text-align: center;
}

.culture-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  margin-top: 41px;
}

.culture-card {
  min-width: 0;
  min-height: 373px;
  padding-bottom: 27px;
  border-bottom: 1px solid #d5d5d5;
}

.culture-card__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 208px;
  overflow: hidden;
  border-radius: 28px;
  background: #f5f5f7;
}

.culture-card__visual img {
  display: block;
  max-width: 124px;
  max-height: 120px;
}

.culture-card h3 {
  margin: 43px 0 11px;
  color: #242424;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
}

.culture-card p {
  max-width: 376px;
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 1.43;
}

.company-history {
  min-height: 1208px;
  padding: 80px 0;
}

.history-timeline {
  position: relative;
  width: 636px;
  margin: 61px auto 0;
  padding: 0;
  list-style: none;
}

.history-timeline::before {
  position: absolute;
  top: 10px;
  bottom: 34px;
  left: 9px;
  width: 1px;
  background: #d4d4d4;
  content: '';
}

.history-item {
  position: relative;
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 35px;
}

.history-item:not(:last-child) {
  margin-bottom: 53px;
}

.history-item__marker {
  position: relative;
  z-index: 1;
  display: block;
  width: 20px;
  height: 20px;
  border: 1px solid #bdbdbd;
  border-radius: 50%;
  background: #fff;
}

.history-item__marker::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #bdbdbd;
  content: '';
  transform: translate(-50%, -50%);
}

.history-item__content time {
  display: block;
  margin: -9px 0 23px;
  color: #bdbdbd;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
}

.history-item__content p {
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 18px;
  line-height: 1.55;
}

.history-item.is-current .history-item__marker {
  border-color: #ef0a6a;
}

.history-item.is-current .history-item__marker::after {
  background: #ef0a6a;
}

.history-item.is-current .history-item__content time,
.history-item.is-current .history-item__content p {
  color: #ef0a6a;
}

.qualification {
  min-height: 505px;
  padding: 80px 0;
}

.qualification-grid {
  display: grid;
  grid-template-columns: repeat(7, 165px);
  justify-content: space-between;
  margin: 91px 0 0;
  padding: 0;
  list-style: none;
}

.qualification-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 165px;
  height: 165px;
  text-align: center;
}

.qualification-item__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 145px;
  height: 100px;
}

.qualification-item__logo img {
  display: block;
  max-width: 100%;
  max-height: 76px;
}

.qualification-item span {
  max-width: 165px;
  min-height: 40px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 1.43;
}

@media (max-width: 900px) {
  .about-container,
  .about-subnav__inner {
    width: calc(100% - 48px);
  }

  .company-profile__inner {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .company-profile__image {
    width: 100%;
    height: auto;
    aspect-ratio: 636 / 356;
  }

  .qualification-grid {
    grid-template-columns: repeat(4, 165px);
    justify-content: center;
    gap: 36px;
  }
}

@media (max-width: 700px) {
  .about-page {
    padding-top: var(--global-header-height, 50px);
  }

  .about-subnav-slot,
  .about-subnav {
    display: none;
  }

  .about-container {
    width: min(400px, calc(100vw - 32px));
    margin: 0 0 0 16px;
  }

  .about-section {
    scroll-margin-top: var(--global-header-height, 50px);
  }

  .section-title,
  .company-profile h1 {
    font-size: 26px;
  }

  .corporate-culture,
  .company-history,
  .qualification {
    min-height: 0;
    padding: 56px 0;
  }

  .company-profile {
    min-height: 0;
    height: auto;
    padding: 0;
  }

  .company-profile__inner {
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: auto;
    padding: 38px 0 56px;
  }

  .company-profile__body {
    margin-top: 2px;
    font-size: 13px;
    line-height: 1.35;
  }

  .company-profile__body p + p {
    margin-top: 8px;
  }

  .company-profile__image {
    order: -1;
    width: 100%;
    height: auto;
    margin: 0;
    border-radius: 18px;
  }

  .culture-grid {
    grid-template-columns: 1fr;
    gap: 32px;
    margin-top: 32px;
  }

  .culture-card {
    min-height: 322px;
    padding-bottom: 0;
  }

  .culture-card__visual {
    height: 200px;
    border-radius: 18px;
  }

  .culture-card h3 {
    margin: 24px 0 10px;
  }

  .history-timeline {
    width: 100%;
    margin-top: 64px;
  }

  .history-item {
    gap: 22px;
  }

  .history-item:not(:last-child) {
    margin-bottom: 44px;
  }

  .history-item__content time {
    margin-bottom: 23px;
    font-size: 30px;
  }

  .history-item__content p {
    font-size: 11px;
    line-height: 1.43;
  }

  .qualification-grid {
    grid-template-columns: repeat(2, minmax(0, 165px));
    justify-content: space-between;
    gap: 24px 16px;
    margin-top: 40px;
  }

  .qualification-item {
    width: 100%;
    max-width: 165px;
    height: 165px;
    overflow: hidden;
    border-radius: 28px;
    background: #fff;
  }
}
</style>
